import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FlaskConical, AlertTriangle, Leaf, Cloud, Calculator, Beaker, Info, CheckCircle2, XCircle,
  FileText, FileSpreadsheet,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#15803d", "#0ea5e9", "#f59e0b", "#ef4444", "#8b5cf6", "#1e293b"];
const SimBadge = () => (
  <Badge className="bg-amber-500 text-white border-0 font-semibold">SIMULASI</Badge>
);
const dash = (v, fmt = rupiah) => (v === null || v === undefined ? <span className="text-slate-400 italic">Data belum tersedia</span> : fmt(v));

function StatMini({ label, value, sub, accent = "slate" }) {
  const map = { green: "text-green-700", blue: "text-blue-700", amber: "text-amber-700", red: "text-red-700", slate: "text-slate-900" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`font-heading font-bold text-lg mt-1 ${map[accent]}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Simulation() {
  const [d, setD] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [sim, setSim] = useState({ semen_substitusi_flyash_pct: 20, waste_reduction_pct: 10, listrik_efficiency_pct: 15, material_price_pct: 0, carbon_tarif_ton: 30000, good_output_units: 100 });
  const [simRes, setSimRes] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const download = async (format) => {
    setDownloading(true);
    try {
      const res = await api.get("/simulation/hpp-001/report", { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SIM-HPP-001.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Laporan ${format.toUpperCase()} diunduh`);
    } catch (e) {
      toast.error("Gagal mengunduh laporan simulasi");
    } finally {
      setDownloading(false);
    }
  };

  const DEFAULT_SIM = { semen_substitusi_flyash_pct: 0, waste_reduction_pct: 0, listrik_efficiency_pct: 0, material_price_pct: 0, carbon_tarif_ton: 30000, good_output_units: 100 };
  const PRESETS = [
    { label: "Substitusi Semen 20%", v: { ...DEFAULT_SIM, semen_substitusi_flyash_pct: 20 } },
    { label: "Pengurangan Waste 10%", v: { ...DEFAULT_SIM, waste_reduction_pct: 10 } },
    { label: "Efisiensi Listrik 15%", v: { ...DEFAULT_SIM, listrik_efficiency_pct: 15 } },
    { label: "Harga −5%", id: "HargaMinus5", v: { ...DEFAULT_SIM, material_price_pct: -5 } },
    { label: "Harga +5%", id: "HargaPlus5", v: { ...DEFAULT_SIM, material_price_pct: 5 } },
    { label: "Harga +10%", id: "HargaPlus10", v: { ...DEFAULT_SIM, material_price_pct: 10 } },
    { label: "Harga +20%", id: "HargaPlus20", v: { ...DEFAULT_SIM, material_price_pct: 20 } },
    { label: "Tarif Karbon 2×", v: { ...DEFAULT_SIM, carbon_tarif_ton: 60000 } },
  ];

  const applyPreset = async (v) => {
    setSim(v);
    try {
      const r = await api.post("/simulation/hpp-001/sensitivity", v);
      setSimRes(r.data);
    } catch (e) {
      toast.error("Gagal menjalankan preset skenario.");
    }
  };

  const load = () => {
    setLoadErr(false);
    api.get("/simulation/hpp-001").then((r) => setD(r.data)).catch(() => setLoadErr(true));
  };
  useEffect(() => { load(); }, []);

  const runSim = async () => {
    try {
      const r = await api.post("/simulation/hpp-001/sensitivity", sim);
      setSimRes(r.data);
    } catch (e) {
      toast.error("Gagal menjalankan skenario sensitivitas. Coba lagi.");
    }
  };

  if (loadErr) return (
    <div className="text-center py-20" data-testid="sim-load-error">
      <p className="text-slate-600 mb-4">Gagal memuat workspace simulasi.</p>
      <Button onClick={load} className="bg-green-700 hover:bg-green-800" data-testid="sim-retry-btn">Coba Lagi</Button>
    </div>
  );
  if (!d) return <div className="text-slate-400 py-20 text-center">Memuat workspace simulasi...</div>;

  const id = d.identity, p = d.produksi, l = d.lingkungan, h = d.hpp, e = d.emisi;
  const doc = e.skenario_dokumen, ref = e.skenario_referensi;

  const costComp = [
    { name: "BBB", value: p.bbb }, { name: "BTKL", value: p.btkl },
    { name: "BOP", value: p.bop }, { name: "Biaya Lingkungan", value: l.bl_operasional },
  ];
  const hppCompare = [
    { name: "HPP Konvensional", value: h.konvensional.per_m3 },
    { name: "Green Operasional", value: h.green_operasional.per_m3 },
    { name: "Green + Karbon (Dok)", value: h.green_skenario_dokumen.per_m3 },
  ];
  const emisiChart = doc.sumber.map((s) => ({ name: s.sumber.split(" ")[0], dokumen: s.emisi, referensi: ref.sumber.find((x) => x.sumber === s.sumber)?.emisi || 0 }));
  const binderChart = [
    { name: "Semen", value: d.binder.semen_pct }, { name: "Fly ash", value: d.binder.fly_ash_pct }, { name: "GGBFS", value: d.binder.ggbfs_pct },
  ];

  return (
    <div className="space-y-6" data-testid="simulation-page">
      {/* Watermark banner */}
      <div className="rounded-xl border-2 border-amber-400 bg-amber-50 px-5 py-3.5 flex items-center gap-3" data-testid="sim-badge-banner">
        <FlaskConical className="h-5 w-5 text-amber-600 shrink-0" />
        <div>
          <p className="font-heading font-bold text-amber-800">{d.badge}</p>
          <p className="text-xs text-amber-700">Lingkungan: {d.environment} · Dataset {id.dataset} · Tidak menghasilkan jurnal, tidak mengubah persediaan aktual.</p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">{id.dataset} <SimBadge /></h2>
          <p className="text-sm text-slate-500 mt-1">{id.nama} — {id.perusahaan}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => download("pdf")} disabled={downloading} className="bg-red-600 hover:bg-red-700" data-testid="sim-download-pdf">
            <FileText className="h-4 w-4 mr-1.5" /> Unduh PDF
          </Button>
          <Button onClick={() => download("excel")} disabled={downloading} className="bg-green-700 hover:bg-green-800" data-testid="sim-download-excel">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Unduh Excel
          </Button>
        </div>
      </div>

      <Tabs defaultValue="ringkasan">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="ringkasan" data-testid="tab-ringkasan">Ringkasan</TabsTrigger>
          <TabsTrigger value="proses" data-testid="tab-proses">Alur Proses</TabsTrigger>
          <TabsTrigger value="produksi" data-testid="tab-produksi">Biaya Produksi</TabsTrigger>
          <TabsTrigger value="lingkungan" data-testid="tab-lingkungan">Biaya Lingkungan</TabsTrigger>
          <TabsTrigger value="emisi" data-testid="tab-emisi">Emisi & Karbon</TabsTrigger>
          <TabsTrigger value="hpp" data-testid="tab-hpp">HPP Terintegrasi</TabsTrigger>
          <TabsTrigger value="sensitivitas" data-testid="tab-sensitivitas">Sensitivitas</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI & Rekonsiliasi</TabsTrigger>
        </TabsList>

        {/* RINGKASAN / DASHBOARD */}
        <TabsContent value="ringkasan" className="space-y-6 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatMini label="BBB" value={rupiah(p.bbb)} />
            <StatMini label="BTKL" value={rupiah(p.btkl)} />
            <StatMini label="BOP" value={rupiah(p.bop)} />
            <StatMini label="BPB (Konvensional)" value={rupiah(p.bpb)} accent="slate" />
            <StatMini label="Biaya Lingkungan Operasional" value={rupiah(l.bl_operasional)} accent="green" />
            <StatMini label="Biaya Karbon Skenario (Dok)" value={rupiah(doc.biaya_karbon_rounded)} sub="Bukan biaya aktual" accent="amber" />
            <StatMini label="BL dengan Karbon (Dok)" value={rupiah(l.bl_dengan_karbon_dokumen)} accent="green" />
            <StatMini label="Green HPP Operasional /m³" value={rupiah(h.green_operasional.per_m3)} accent="green" />
            <StatMini label="HPP Konvensional /m³" value={rupiah(h.konvensional.per_m3)} />
            <StatMini label="Green + Karbon /m³ (Dok)" value={rupiah(h.green_skenario_dokumen.per_m3)} accent="green" />
            <StatMini label="HPP Terintegrasi /unit" value={rupiah(h.green_skenario_dokumen.per_unit)} accent="blue" />
            <StatMini label="Manfaat Daur Ulang" value={rupiah(Math.abs(l.bdr.total))} accent="green" />
            <StatMini label="Emisi Skenario Dokumen" value={`${num(doc.total_kg, 1)} kgCO₂e`} accent="red" />
            <StatMini label="Emisi Skenario Referensi" value={`${num(ref.total_kg, 2)} kgCO₂e`} accent="red" />
            <StatMini label="Status Inventori Emisi" value="Parsial" accent="amber" />
            <StatMini label="Status AI" value="Belum siap" accent="amber" />
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">Komposisi Biaya (Operasional)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={costComp} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(x) => x.name}>
                    {costComp.map((c) => <Cell key={c.name} fill={COLORS[costComp.indexOf(c)]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => rupiah(v)} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">HPP Konvensional vs Green (per m³)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hppCompare}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(2)}jt`} />
                  <Tooltip formatter={(v) => rupiah(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    <Cell fill="#94a3b8" /><Cell fill="#15803d" /><Cell fill="#0ea5e9" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">Emisi per Sumber (kgCO₂e)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={emisiChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="dokumen" name="Skenario Dokumen" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="referensi" name="Skenario Referensi" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">Komposisi Binder (%)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={binderChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(x) => `${x.name} ${x.value}%`}>
                    {binderChart.map((c) => <Cell key={c.name} fill={COLORS[binderChart.indexOf(c)]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </TabsContent>

        {/* PROSES */}
        <TabsContent value="proses" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold mb-1">Identitas Simulasi</h3>
            <div className="grid sm:grid-cols-3 gap-3 text-sm mb-6 mt-3">
              {[["No. Pesanan", id.no_pesanan], ["No. Batch", id.no_batch], ["Produk", id.produk],
                ["Volume", `${id.volume} m³`], ["Jumlah Unit", `${id.unit} unit`], ["Volume/Unit", `${id.volume_per_unit} m³`],
                ["Pelanggan", id.pelanggan], ["Harga Jual", null], ["Kuat Tekan / Mutu", null]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-[11px] text-slate-500">{k}</p>
                  <p className="font-medium text-slate-800">{dash(v, (x) => x)}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">{id.catatan_mutu}</p>
            <h3 className="font-heading font-semibold mb-3">Alur Siklus Hidup HPP</h3>
            <div className="flex flex-wrap gap-2">
              {d.proses.map((step, i) => (
                <div key={step} className="flex items-center gap-2">
                  <span className="text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-800 border border-green-200">{i + 1}. {step}</span>
                  {i < d.proses.length - 1 && <span className="text-slate-300">→</span>}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* PRODUKSI */}
        <TabsContent value="produksi" className="space-y-6 mt-4">
          <Card className="p-5 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold mb-3">Komposisi Material per Batch <SimBadge /></h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>Material</TableHead><TableHead className="text-right">Kuantitas</TableHead><TableHead>Satuan</TableHead>
                  <TableHead className="text-right">Harga Simulasi</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Per m³</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {d.materials.map((m) => (
                    <TableRow key={m.nama}><TableCell className="font-medium">{m.nama}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(m.kuantitas)}</TableCell><TableCell>{m.satuan}</TableCell>
                      <TableCell className="text-right tabular-nums">{rupiah(m.harga)}</TableCell>
                      <TableCell className="text-right tabular-nums">{rupiah(m.total)}</TableCell><TableCell>{m.per_m3}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-green-50 font-semibold"><TableCell colSpan={4}>Total BBB</TableCell><TableCell className="text-right tabular-nums">{rupiah(p.bbb)}</TableCell><TableCell /></TableRow>
                </TableBody>
              </Table>
            </div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
              <StatMini label="Total Binder /m³" value="400 kg" accent="green" />
              <StatMini label="Rasio Air/Binder" value={d.binder.w_b_ratio} accent="blue" />
              <StatMini label="Semen" value={`${d.binder.semen_pct}%`} />
              <StatMini label="Fly ash" value={`${d.binder.fly_ash_pct}%`} accent="green" />
              <StatMini label="GGBFS" value={`${d.binder.ggbfs_pct}%`} accent="green" />
              <StatMini label="Substitusi Semen" value={`${d.binder.substitusi_semen_pct}%`} accent="green" />
            </div>
            <p className="text-xs text-slate-500 mt-3 italic">Label: {d.binder.label}. Tidak diklaim memenuhi SNI sebelum ada hasil pengujian mutu.</p>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">Tenaga Kerja Langsung (BTKL)</h3>
              <Table><TableBody>
                {d.labor.items.map((x) => <TableRow key={x.komponen}><TableCell>{x.komponen}</TableCell><TableCell className="text-right tabular-nums">{rupiah(x.total)}</TableCell></TableRow>)}
                <TableRow className="bg-green-50 font-semibold"><TableCell>Total BTKL</TableCell><TableCell className="text-right tabular-nums">{rupiah(d.labor.total)}</TableCell></TableRow>
              </TableBody></Table>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">{d.labor.catatan}</p>
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-3">Overhead Produksi (BOP)</h3>
              <Table><TableBody>
                {d.overhead.items.map((x) => <TableRow key={x.komponen}><TableCell>{x.komponen}</TableCell><TableCell className="text-right tabular-nums">{rupiah(x.total)}</TableCell></TableRow>)}
                <TableRow className="bg-green-50 font-semibold"><TableCell>Total BOP</TableCell><TableCell className="text-right tabular-nums">{rupiah(d.overhead.total)}</TableCell></TableRow>
              </TableBody></Table>
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 mt-3">{d.overhead.catatan}</p>
            </Card>
          </div>
          <Card className="p-5 border-slate-200 shadow-sm bg-slate-900 text-white">
            <div className="grid sm:grid-cols-3 gap-4">
              <StatMini label="BPB per Batch" value={rupiah(p.bpb)} />
              <StatMini label="BPB per m³" value={rupiah(p.bpb_detail.per_m3)} />
              <StatMini label="BPB per Unit" value={rupiah(p.bpb_detail.per_unit)} />
            </div>
          </Card>
        </TabsContent>

        {/* LINGKUNGAN */}
        <TabsContent value="lingkungan" className="space-y-6 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {[["Pengelolaan Limbah (BPL)", l.bpl], ["Energi & Utilitas Lingkungan (BEU)", l.beu],
              ["Daur Ulang / Reuse (BDR)", l.bdr], ["Pengendalian Lingkungan (BPLg)", l.bplg]].map(([title, sec]) => (
              <Card key={title} className="p-5 border-slate-200 shadow-sm">
                <h3 className="font-heading font-semibold mb-3">{title}</h3>
                <Table><TableBody>
                  {sec.items.map((x) => <TableRow key={x.komponen}><TableCell>{x.komponen}{x.aktivitas ? ` (${x.aktivitas})` : ""}</TableCell><TableCell className="text-right tabular-nums">{rupiah(x.total)}</TableCell></TableRow>)}
                  <TableRow className="bg-green-50 font-semibold"><TableCell>Total</TableCell><TableCell className="text-right tabular-nums">{rupiah(sec.total)}</TableCell></TableRow>
                </TableBody></Table>
                {sec.catatan && <p className="text-xs text-slate-500 mt-2">{sec.catatan}</p>}
                {sec.label && <p className="text-xs text-green-700 font-medium mt-2">{sec.label}</p>}
              </Card>
            ))}
          </div>
          <Card className="p-5 border-slate-200 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <StatMini label="Biaya Lingkungan Operasional (tanpa karbon)" value={rupiah(l.bl_operasional)} accent="green" sub={`per m³ ${rupiah(l.bl_detail_operasional.per_m3)} · per unit ${rupiah(l.bl_detail_operasional.per_unit)}`} />
              <StatMini label="Biaya Lingkungan dengan Karbon (Dokumen)" value={rupiah(l.bl_dengan_karbon_dokumen)} accent="amber" sub={`per m³ ${rupiah(l.bl_detail_dokumen.per_m3)} · per unit ${rupiah(l.bl_detail_dokumen.per_unit)}`} />
            </div>
          </Card>
        </TabsContent>

        {/* EMISI */}
        <TabsContent value="emisi" className="space-y-6 mt-4">
          <div className="grid lg:grid-cols-2 gap-6">
            {[doc, ref].map((sc) => (
              <Card key={sc.kode} className="p-5 border-slate-200 shadow-sm">
                <h3 className="font-heading font-semibold flex items-center gap-2">{sc.kode} <Badge className="bg-blue-100 text-blue-800 border-blue-200 border text-[10px]">SKENARIO</Badge></h3>
                <p className="text-xs text-slate-500 mb-3">Faktor: {sc.label_faktor}</p>
                <Table><TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50"><TableHead>Sumber</TableHead><TableHead>Aktivitas</TableHead><TableHead className="text-right">Faktor</TableHead><TableHead className="text-right">kgCO₂e</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {sc.sumber.map((s) => <TableRow key={s.sumber}><TableCell>{s.sumber}</TableCell><TableCell>{s.aktivitas}</TableCell><TableCell className="text-right tabular-nums">{s.faktor}</TableCell><TableCell className="text-right tabular-nums">{num(s.emisi, 2)}</TableCell></TableRow>)}
                    <TableRow className="bg-red-50 font-semibold"><TableCell colSpan={3}>Total Emisi</TableCell><TableCell className="text-right tabular-nums">{num(sc.total_kg, 2)} kg</TableCell></TableRow>
                  </TableBody></Table>
                <div className="mt-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Total ton CO₂e</span><span className="tabular-nums">{num(sc.total_ton, 4)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tarif karbon</span><span className="tabular-nums">{rupiah(sc.tarif_karbon_per_ton)}/ton ({sc.tarif_label})</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Biaya karbon (presisi)</span><span className="tabular-nums">{rupiah(sc.biaya_karbon_precise)}</span></div>
                  {sc.biaya_karbon_rounded !== undefined && <div className="flex justify-between font-semibold"><span>Biaya karbon (pembulatan laporan)</span><span className="tabular-nums text-amber-700">{rupiah(sc.biaya_karbon_rounded)}</span></div>}
                </div>
                {sc.biaya_karbon_catatan && <p className="text-xs text-red-600 mt-2">⚠ {sc.biaya_karbon_catatan}</p>}
              </Card>
            ))}
          </div>
          <Card className="p-5 border-amber-200 bg-amber-50 shadow-sm">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-heading font-semibold text-amber-900">{e.inventori.nama}</h3>
                <p className="text-sm text-amber-800 mt-1">Boundary: {e.inventori.boundary}</p>
                <p className="text-sm text-amber-800">Kelengkapan faktor material: <b>{e.inventori.kelengkapan_faktor_material}</b></p>
                <p className="text-xs text-amber-700 mt-2">{e.inventori.catatan}</p>
                <p className="text-xs text-amber-700 mt-2">Faktor belum tersedia: {e.inventori.faktor_belum_tersedia.join(", ")}</p>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* HPP */}
        <TabsContent value="hpp" className="space-y-6 mt-4">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 border-slate-200 shadow-sm"><div className="flex items-center gap-2 mb-3"><Calculator className="h-5 w-5 text-slate-600" /><h3 className="font-heading font-semibold">HPP Konvensional</h3></div>
              <StatMini label="Per Batch" value={rupiah(h.konvensional.batch)} /><div className="mt-2" /><StatMini label="Per m³" value={rupiah(h.konvensional.per_m3)} /><div className="mt-2" /><StatMini label="Per Unit" value={rupiah(h.konvensional.per_unit)} />
            </Card>
            <Card className="p-6 border-green-200 shadow-sm bg-green-50/40"><div className="flex items-center gap-2 mb-3"><Leaf className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold">Green HPP Operasional</h3></div>
              <StatMini label="Per Batch" value={rupiah(h.green_operasional.batch)} accent="green" /><div className="mt-2" /><StatMini label="Per m³" value={rupiah(h.green_operasional.per_m3)} accent="green" /><div className="mt-2" /><StatMini label="Per Unit" value={rupiah(h.green_operasional.per_unit)} accent="green" />
              <p className="text-[11px] text-slate-500 mt-2">Tanpa monetisasi karbon (biaya lingkungan aktual dapat dialokasikan).</p>
            </Card>
            <Card className="p-6 border-blue-200 shadow-sm bg-blue-50/40"><div className="flex items-center gap-2 mb-3"><Cloud className="h-5 w-5 text-blue-700" /><h3 className="font-heading font-semibold">Green HPP + Skenario Karbon</h3></div>
              <StatMini label="Per Batch" value={rupiah(h.green_skenario_dokumen.batch)} accent="blue" /><div className="mt-2" /><StatMini label="Per m³" value={rupiah(h.green_skenario_dokumen.per_m3)} accent="blue" /><div className="mt-2" /><StatMini label="Per Unit" value={rupiah(h.green_skenario_dokumen.per_unit)} accent="blue" />
              <p className="text-[11px] text-slate-500 mt-2">Skenario dokumen (+ biaya karbon Rp53.000). Bukan biaya aktual.</p>
            </Card>
          </div>
          <Card className="p-5 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold mb-3">Skenario Referensi (pembanding, baseline tidak berubah)</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <StatMini label="Per Batch" value={rupiah(h.green_skenario_referensi.batch)} />
              <StatMini label="Per m³" value={rupiah(h.green_skenario_referensi.per_m3)} />
              <StatMini label="Per Unit" value={rupiah(h.green_skenario_referensi.per_unit)} />
            </div>
          </Card>
        </TabsContent>

        {/* SENSITIVITAS */}
        <TabsContent value="sensitivitas" className="mt-4 space-y-4">
          <Card className="p-4 border-slate-200 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 mb-2">Preset Skenario Cepat</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((ps) => (
                <Button key={ps.label} variant="outline" size="sm" onClick={() => applyPreset(ps.v)}
                  data-testid={`sim-preset-${ps.id || ps.label.replace(/[^a-zA-Z0-9]/g, "")}`}
                  className="border-green-200 text-green-800 hover:bg-green-50">
                  {ps.label}
                </Button>
              ))}
            </div>
          </Card>
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-5 border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center gap-2"><Beaker className="h-5 w-5 text-green-700" /><h3 className="font-heading font-semibold">Parameter Skenario</h3></div>
              {[["semen_substitusi_flyash_pct", "Substitusi Semen → Fly Ash (%)"], ["waste_reduction_pct", "Pengurangan Waste (%)"], ["listrik_efficiency_pct", "Efisiensi Listrik (%)"], ["material_price_pct", "Perubahan Harga Material (%)"], ["carbon_tarif_ton", "Tarif Karbon (Rp/ton)"], ["good_output_units", "Output Produk Baik (unit)"]].map(([k, lbl]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{lbl}</Label>
                  <Input type="number" value={sim[k]} onChange={(ev) => setSim({ ...sim, [k]: Number(ev.target.value) })} data-testid={`sim-param-${k}`} /></div>
              ))}
              <Button onClick={runSim} className="w-full bg-green-700 hover:bg-green-800" data-testid="sim-run-btn">Jalankan Skenario</Button>
              <p className="text-[11px] text-amber-700">Status: Memerlukan Validasi Mutu. Tidak mengubah baseline & tidak menghasilkan jurnal.</p>
            </Card>
            <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold mb-4">Hasil Sensitivitas (Sebelum vs Sesudah)</h3>
              {!simRes ? <p className="text-sm text-slate-400">Jalankan skenario untuk melihat perbandingan.</p> : (
                <>
                  <Table><TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50"><TableHead>Indikator</TableHead><TableHead className="text-right">Sebelum</TableHead><TableHead className="text-right">Sesudah</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[["BPB (Konvensional)", rupiah(simRes.before.bpb), rupiah(simRes.after.bpb)],
                        ["Green Operasional / batch", rupiah(simRes.before.green_operasional_batch), rupiah(simRes.after.green_operasional_batch)],
                        ["Green + Karbon / batch", rupiah(simRes.before.green_skenario_batch), rupiah(simRes.after.green_skenario_batch)],
                        ["Green + Karbon / unit", rupiah(simRes.before.per_unit), rupiah(simRes.after.green_skenario_per_unit)],
                        ["Emisi (kgCO₂e)", num(simRes.before.emisi_kg, 2), num(simRes.after.emisi_kg, 2)]].map((r) => (
                        <TableRow key={r[0]}><TableCell>{r[0]}</TableCell><TableCell className="text-right tabular-nums text-slate-500">{r[1]}</TableCell><TableCell className="text-right tabular-nums font-medium text-green-700">{r[2]}</TableCell></TableRow>
                      ))}
                    </TableBody></Table>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className={`p-4 rounded-lg text-center ${simRes.selisih_hpp_batch >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                      <p className={`text-xs ${simRes.selisih_hpp_batch >= 0 ? "text-green-700" : "text-red-700"}`}>{simRes.selisih_hpp_batch >= 0 ? "Penghematan HPP / batch" : "Kenaikan HPP / batch"}</p>
                      <p className={`font-heading font-bold text-lg ${simRes.selisih_hpp_batch >= 0 ? "text-green-700" : "text-red-700"}`}>{rupiah(Math.abs(simRes.selisih_hpp_batch))}</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${simRes.selisih_emisi_kg >= 0 ? "bg-blue-50" : "bg-amber-50"}`}>
                      <p className={`text-xs ${simRes.selisih_emisi_kg >= 0 ? "text-blue-700" : "text-amber-700"}`}>{simRes.selisih_emisi_kg >= 0 ? "Reduksi Emisi" : "Kenaikan Emisi"}</p>
                      <p className={`font-heading font-bold text-lg ${simRes.selisih_emisi_kg >= 0 ? "text-blue-700" : "text-amber-700"}`}>{num(Math.abs(simRes.selisih_emisi_kg), 2)} kg</p>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* AI & REKONSILIASI */}
        <TabsContent value="ai" className="space-y-6 mt-4">
          <Card className="p-6 border-amber-200 bg-amber-50 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-heading font-semibold text-amber-900">AI Readiness — Belum Siap</h3>
                <p className="text-sm text-amber-800 mt-1">{d.ai_readiness.pesan}</p>
                <p className="text-sm text-amber-800 mt-2">Metode yang digunakan (deterministik, bukan model terlatih):</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {d.ai_readiness.metode_dipakai.map((m) => <Badge key={m} className="bg-white text-amber-800 border-amber-300 border">{m}</Badge>)}
                </div>
                <p className="text-xs text-amber-700 mt-3">Tidak menampilkan akurasi/MAE/RMSE/R²/feature importance palsu.</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold mb-4">Cost Driver Dominan</h3>
            {d.cost_drivers.map((c) => {
              const totalCost = p.bpb;
              const pctv = (c.total / totalCost) * 100;
              return (
                <div key={c.nama} className="mb-2.5">
                  <div className="flex justify-between text-sm"><span className="text-slate-600">{c.nama}</span><span className="font-medium">{rupiah(c.total)} ({pctv.toFixed(1)}%)</span></div>
                  <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-full bg-green-600 rounded-full" style={{ width: `${pctv}%` }} /></div>
                </div>
              );
            })}
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <h3 className="font-heading font-semibold mb-4">Rekonsiliasi Perhitungan (target dokumen)</h3>
            <div className="overflow-x-auto">
              <Table><TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50"><TableHead>Item</TableHead><TableHead className="text-right">Nilai Sistem</TableHead><TableHead className="text-right">Target Dokumen</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {[["BBB", p.bbb, 59500000], ["BTKL", p.btkl, 2360000], ["BOP", p.bop, 6500000], ["BPB", p.bpb, 68360000],
                    ["BPL", l.bpl.total, 1900000], ["BEU", l.beu.total, 1690000], ["BDR", l.bdr.total, -300000], ["BPLg", l.bplg.total, 1050000],
                    ["Emisi dokumen (kg)", doc.total_kg, 1756.6], ["Biaya karbon dok (bulat)", doc.biaya_karbon_rounded, 53000],
                    ["BL dokumen", l.bl_dengan_karbon_dokumen, 4393000], ["HPP terintegrasi", h.green_skenario_dokumen.batch, 72753000],
                    ["HPP /m³", h.green_skenario_dokumen.per_m3, 1455060], ["HPP /unit", h.green_skenario_dokumen.per_unit, 727530],
                    ["Emisi referensi (kg)", ref.total_kg, 1691.01]].map((r) => {
                    const ok = Math.abs(r[1] - r[2]) < 0.5;
                    return (
                      <TableRow key={r[0]}><TableCell>{r[0]}</TableCell>
                        <TableCell className="text-right tabular-nums">{num(r[1], 2)}</TableCell>
                        <TableCell className="text-right tabular-nums">{num(r[2], 2)}</TableCell>
                        <TableCell>{ok ? <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Cocok</span> : <span className="text-red-600 inline-flex items-center gap-1"><XCircle className="h-4 w-4" /> Selisih</span>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody></Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
